import { Capacitor } from '@capacitor/core';

export interface StoredCredentials {
  email: string;
  password: string;
}

// Use app-specific prefix to prevent collisions with other apps
// Using the app bundle ID ensures unique storage namespace
const STORAGE_PREFIX = 'com.musterix.idatech.';

const CREDENTIALS_KEY = `${STORAGE_PREFIX}userCredentials`;
const BIOMETRIC_ENABLED_KEY = `${STORAGE_PREFIX}biometricEnabled`;
// Flag stored in localStorage to check if credentials exist without prompting for biometrics
const CREDENTIALS_EXIST_FLAG = `${STORAGE_PREFIX}credentialsExist`;

type NativeBiometricPlugin = {
  isAvailable: () => Promise<{ isAvailable: boolean }>;
  verifyIdentity: (options: {
    reason: string;
    title: string;
    subtitle: string;
    description: string;
    negativeButtonText: string;
  }) => Promise<void>;
  setCredentials: (options: { username: string; password: string; server: string }) => Promise<void>;
  getCredentials: (options: { server: string }) => Promise<{ username?: string; password?: string }>;
  deleteCredentials: (options: { server: string }) => Promise<void>;
};

async function loadNativeBiometric(): Promise<NativeBiometricPlugin | null> {
  try {
    const moduleName = '@capgo/capacitor-native-biometric';
    const biometricsModule = await import(/* @vite-ignore */ moduleName);
    return (biometricsModule as { NativeBiometric?: NativeBiometricPlugin }).NativeBiometric ?? null;
  } catch (error) {
    console.warn('[Biometric] Native biometric plugin unavailable:', error);
    return null;
  }
}

/**
 * Check if biometric authentication is available on the device
 */
export async function isBiometricAvailable(): Promise<boolean> {
  try {
    console.log('[Biometric] Checking biometric availability...');
    console.log('[Biometric] Platform:', Capacitor.getPlatform());
    console.log('[Biometric] Is native:', Capacitor.isNativePlatform());
    
    if (!Capacitor.isNativePlatform()) {
      console.log('[Biometric] Not on native platform, returning false');
      return false;
    }
    const biometric = await loadNativeBiometric();
    if (!biometric) return false;
    
    console.log('[Biometric] Calling NativeBiometric.isAvailable()...');
    const result = await biometric.isAvailable();
    console.log('[Biometric] Availability result:', JSON.stringify(result, null, 2));
    console.log('[Biometric] Is available:', result.isAvailable);
    return result.isAvailable;
  } catch (error: any) {
    console.error('[Biometric] Error checking biometric availability:', error);
    console.error('[Biometric] Error type:', error instanceof Error ? error.constructor.name : typeof error);
    console.error('[Biometric] Error message:', error instanceof Error ? error.message : String(error));
    if (error instanceof Error && error.stack) {
      console.error('[Biometric] Error stack:', error.stack);
    }
    
    // Return false on error
    return false;
  }
}

/**
 * Authenticate using biometrics (Face ID, Touch ID, or device credentials)
 */
export async function authenticateWithBiometrics(reason: string = 'Giriş yapmak için kimliğinizi doğrulayın'): Promise<boolean> {
  try {
    console.log('[Biometric] Starting biometric authentication...');
    console.log('[Biometric] Reason:', reason);
    console.log('[Biometric] Platform:', Capacitor.getPlatform());
    console.log('[Biometric] Is native:', Capacitor.isNativePlatform());
    
    if (!Capacitor.isNativePlatform()) {
      console.log('[Biometric] Not on native platform, cannot authenticate');
      return false;
    }
    const biometric = await loadNativeBiometric();
    if (!biometric) return false;
    
    console.log('[Biometric] Calling NativeBiometric.verifyIdentity()...');
    // verifyIdentity resolves on success and rejects on failure/cancel
    // So we use .then()/.catch() pattern to determine success
    try {
      await biometric.verifyIdentity({
        reason,
        title: 'Kimlik Doğrulama',
        subtitle: 'Giriş yapmak için kimliğinizi doğrulayın',
        description: '',
        negativeButtonText: 'İptal',
      });
      // If we reach here, authentication was successful
      console.log('[Biometric] Authentication succeeded');
      return true;
    } catch (verifyError: any) {
      // If promise rejects, authentication failed or was cancelled
      console.log('[Biometric] verifyIdentity rejected (user cancelled or failed)');
      // Re-throw to be caught by outer catch block for proper error handling
      throw verifyError;
    }
  } catch (error: any) {
    console.error('[Biometric] Biometric authentication failed:', error);
    console.error('[Biometric] Error type:', error instanceof Error ? error.constructor.name : typeof error);
    console.error('[Biometric] Error message:', error instanceof Error ? error.message : String(error));
    if (error instanceof Error && error.stack) {
      console.error('[Biometric] Error stack:', error.stack);
    }
    
    // User cancellation is not an error, just return false
    if (error?.code === 'USER_CANCEL' || error?.message?.includes('cancel')) {
      console.log('[Biometric] User cancelled authentication');
      return false;
    }
    
    return false;
  }
}

/**
 * Store user credentials securely using NativeBiometric
 * This plugin stores credentials securely in the iOS Keychain or Android Keystore
 */
export async function storeCredentials(email: string, password: string): Promise<boolean> {
  try {
    console.log('[Biometric] Storing credentials...');
    console.log('[Biometric] Platform:', Capacitor.getPlatform());
    console.log('[Biometric] Is native:', Capacitor.isNativePlatform());
    console.log('[Biometric] Email to store:', email);
    console.log('[Biometric] Password length:', password.length);
    
    // Check if we're on a native platform
    if (!Capacitor.isNativePlatform()) {
      console.log('[Biometric] Not on native platform, cannot store credentials');
      return false;
    }
    const biometric = await loadNativeBiometric();
    if (!biometric) return false;
    
    // Validate inputs
    const trimmedEmail = email?.trim();
    const trimmedPassword = password?.trim();
    
    if (!trimmedEmail || !trimmedPassword) {
      console.error('[Biometric] Email or password is empty after trimming');
      return false;
    }
    
    // Ensure values are strings (explicit conversion)
    const emailValue = String(trimmedEmail);
    const passwordValue = String(trimmedPassword);
    
    // Create credentials object
    const credentials: StoredCredentials = {
      email: emailValue,
      password: passwordValue,
    };
    
    console.log('[Biometric] Storing credentials with NativeBiometric...');
    
    // Store credentials securely using NativeBiometric
    // This uses iOS Keychain or Android Keystore with biometric protection
    await biometric.setCredentials({
      username: emailValue,
      password: passwordValue,
      server: CREDENTIALS_KEY, // Use as server identifier for the credentials
    });
    
    console.log('[Biometric] Credentials stored successfully');
    
    // Set a flag in localStorage to indicate credentials are stored
    // This allows us to check if credentials exist without prompting for biometrics
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(CREDENTIALS_EXIST_FLAG, 'true');
        console.log('[Biometric] Credentials exist flag set in localStorage');
      }
    } catch (flagError) {
      console.log('[Biometric] Could not set credentials exist flag:', flagError);
    }
    
    // Also set a flag in NativeBiometric for backward compatibility
    try {
      await biometric.setCredentials({
        username: 'biometric_enabled',
        password: 'true',
        server: BIOMETRIC_ENABLED_KEY,
      });
      console.log('[Biometric] Biometric enabled flag set');
    } catch (flagError) {
      // Ignore errors for the flag, it's not critical
      console.log('[Biometric] Could not set biometric enabled flag:', flagError);
    }
    
    return true;
  } catch (error: any) {
    console.error('[Biometric] Failed to store credentials:', error);
    console.error('[Biometric] Error details:', JSON.stringify(error, null, 2));
    console.error('[Biometric] Error code:', error?.code);
    console.error('[Biometric] Error name:', error?.name);
    if (error instanceof Error) {
      console.error('[Biometric] Error message:', error.message);
      console.error('[Biometric] Error stack:', error.stack);
    }
    
    return false;
  }
}

/**
 * Retrieve stored credentials using NativeBiometric
 */
export async function getStoredCredentials(): Promise<StoredCredentials | null> {
  try {
    console.log('[Biometric] Retrieving stored credentials...');
    
    if (!Capacitor.isNativePlatform()) {
      console.log('[Biometric] Not on native platform, returning null');
      return null;
    }
    const biometric = await loadNativeBiometric();
    if (!biometric) return null;
    
    // Retrieve credentials from NativeBiometric
    // This will prompt for biometric authentication if required
    console.log('[Biometric] Getting credentials from NativeBiometric...');
    const result = await biometric.getCredentials({
      server: CREDENTIALS_KEY,
    });
    
    if (result && result.username && result.password) {
      console.log('[Biometric] Credentials retrieved successfully');
      const credentials: StoredCredentials = {
        email: result.username,
        password: result.password,
      };
      console.log('[Biometric] Credentials retrieved:', { email: credentials.email, password: '***' });
      return credentials;
    }
    
    console.log('[Biometric] No credentials found');
    return null;
  } catch (error: any) {
    console.error('[Biometric] Failed to retrieve credentials:', error);
    console.error('[Biometric] Error code:', error?.code);
    if (error instanceof Error) {
      console.error('[Biometric] Error message:', error.message);
      console.error('[Biometric] Error stack:', error.stack);
    }
    
    // If credentials not found, return null (not an error)
    if (error?.code === 'NOT_FOUND' || error?.message?.includes('not found')) {
      console.log('[Biometric] Credentials not found');
      return null;
    }
    
    // Return null on error to prevent blocking login
    return null;
  }
}

/**
 * Check if biometric login is enabled
 */
export async function isBiometricEnabled(): Promise<boolean> {
  try {
    console.log('[Biometric] Checking if biometric is enabled...');
    
    if (!Capacitor.isNativePlatform()) {
      console.log('[Biometric] Not on native platform, returning false');
      return false;
    }
    
    // Check if credentials exist (if they exist, biometric is enabled)
    const credentials = await getStoredCredentials();
    const enabled = credentials !== null;
    console.log('[Biometric] Is biometric enabled:', enabled);
    return enabled;
  } catch (error) {
    console.error('[Biometric] Failed to check biometric enabled status:', error);
    if (error instanceof Error) {
      console.error('[Biometric] Error message:', error.message);
      console.error('[Biometric] Error stack:', error.stack);
    }
    // Return false on error to prevent blocking login
    return false;
  }
}

/**
 * Check if credentials are already saved (without prompting for biometrics)
 * This checks the localStorage flag instead of actually retrieving credentials
 */
export async function hasCredentialsSaved(): Promise<boolean> {
  try {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }
    
    // Check the localStorage flag - this doesn't require biometric authentication
    if (typeof window !== 'undefined' && window.localStorage) {
      const flag = localStorage.getItem(CREDENTIALS_EXIST_FLAG);
      const hasCredentials = flag === 'true';
      console.log('[Biometric] Credentials exist flag:', hasCredentials);
      return hasCredentials;
    }
    
    return false;
  } catch (error) {
    console.error('[Biometric] Error checking if credentials are saved:', error);
    return false;
  }
}

/**
 * Clear stored credentials
 */
export async function clearStoredCredentials(): Promise<void> {
  try {
    console.log('[Biometric] Clearing stored credentials...');
    
    // Clear the localStorage flag
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(CREDENTIALS_EXIST_FLAG);
        console.log('[Biometric] Credentials exist flag cleared from localStorage');
      }
    } catch (error) {
      console.log('[Biometric] Error clearing localStorage flag:', error);
    }
    
    const biometric = await loadNativeBiometric();
    if (!biometric) return;

    // Delete credentials from NativeBiometric
    try {
      await biometric.deleteCredentials({
        server: CREDENTIALS_KEY,
      });
    } catch (error) {
      console.log('[Biometric] Error deleting credentials (may not exist):', error);
    }
    
    // Delete biometric enabled flag
    try {
      await biometric.deleteCredentials({
        server: BIOMETRIC_ENABLED_KEY,
      });
    } catch (error) {
      console.log('[Biometric] Error deleting flag (may not exist):', error);
    }
    
    console.log('[Biometric] Credentials cleared successfully');
  } catch (error) {
    console.error('[Biometric] Failed to clear credentials:', error);
    console.error('[Biometric] Error details:', JSON.stringify(error, null, 2));
  }
}
